import React, { useState, useEffect } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'https://drvivara.cloud/api' });
api.interceptors.request.use(c => { const t = localStorage.getItem('token'); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const ReelsAdmin = () => {
  const [reels, setReels] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => { fetchReels(); fetchAnalytics(); }, [filter]);

  const fetchReels = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/reels', { params: { status: filter } });
      if (res.data.success) setReels(res.data.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await api.get('/admin/reels/analytics');
      if (res.data.success) setAnalytics(res.data.data);
    } catch (e) { /* ignore */ }
  };

  const approve = async (id) => { try { const r = await api.put(`/admin/reels/${id}/approve`); if (r.data.success) { setMessage('Approved'); fetchReels(); } } catch (e) { setMessage('Failed'); } };
  const reject = async (id) => { const reason = prompt('Rejection reason:'); try { const r = await api.put(`/admin/reels/${id}/reject`, { reason }); if (r.data.success) { setMessage('Rejected'); fetchReels(); } } catch (e) { setMessage('Failed'); } };
  const deleteReel = async (id) => { if (!window.confirm('Soft delete this reel?')) return; try { const r = await api.delete(`/admin/reels/${id}`); if (r.data.success) { setMessage('Deleted'); fetchReels(); } } catch (e) { setMessage('Failed'); } };
  const hardDeleteReel = async (id) => { if (!window.confirm('PERMANENTLY delete this reel from database? This cannot be undone!')) return; try { const r = await api.delete(`/admin/reels/${id}/hard`); if (r.data.success) { setMessage('Permanently deleted'); fetchReels(); } } catch (e) { setMessage('Failed'); } };
  const toggleFeature = async (id) => { try { const r = await api.put(`/admin/reels/${id}/feature`); if (r.data.success) { setMessage('Toggled feature'); fetchReels(); } } catch (e) { setMessage('Failed'); } };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-gold mb-6">Reels Management</h1>
      {message && <p className="text-green-400 mb-4">{message}</p>}

      {analytics && (
        <div className="grid grid-cols-5 gap-4 mb-6">
          {[
            { label: 'Total Reels', value: analytics.totalReels },
            { label: 'Pending', value: analytics.pendingReels },
            { label: 'Approved', value: analytics.approvedReels },
            { label: 'Interactions', value: analytics.totalInteractions },
            { label: 'Creators', value: analytics.totalCreators },
          ].map(s => (
            <div key={s.label} className="bg-gray-900 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gold">{s.value}</p>
              <p className="text-xs text-gray-400">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {['pending', 'approved', 'rejected', 'all'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded text-sm ${filter === f ? 'bg-gold text-dark' : 'bg-gray-800 text-light'}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading ? <p className="text-center text-gold">Loading...</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead><tr className="text-gray-400 border-b border-gray-700">
              <th className="py-2 px-3">Creator</th><th className="py-2 px-3">Caption</th>
              <th className="py-2 px-3">Status</th><th className="py-2 px-3">Featured</th>
              <th className="py-2 px-3">Analytics</th><th className="py-2 px-3">Actions</th>
            </tr></thead>
            <tbody>
              {reels.map(reel => (
                <tr key={reel._id} className="border-b border-gray-800 hover:bg-gray-800/50">
                  <td className="py-2 px-3 text-light">{reel.creator_id?.name || 'Unknown'}</td>
                  <td className="py-2 px-3 text-gray-400 max-w-[200px] truncate">{reel.caption || '-'}</td>
                  <td className="py-2 px-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${reel.status === 'approved' ? 'bg-green-600' : reel.status === 'rejected' ? 'bg-red-600' : 'bg-yellow-600'}`}>
                      {reel.status}
                    </span>
                  </td>
                  <td className="py-2 px-3">{reel.is_featured ? '⭐' : '-'}</td>
                  <td className="py-2 px-3 text-xs text-gray-400">
                    {reel.analytics ? `❤️${reel.analytics.likes} 💬${reel.analytics.comments} 🔗${reel.analytics.shares}` : '-'}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex gap-2">
                      {reel.status === 'pending' && (
                        <>
                          <button onClick={() => approve(reel._id)} className="text-green-400 text-xs">Approve</button>
                          <button onClick={() => reject(reel._id)} className="text-red-400 text-xs">Reject</button>
                        </>
                      )}
                      <button onClick={() => toggleFeature(reel._id)} className="text-yellow-400 text-xs">Feature</button>
                      <button onClick={() => deleteReel(reel._id)} className="text-red-400 text-xs">Delete</button>
                      <button onClick={() => hardDeleteReel(reel._id)} className="text-red-600 text-xs font-bold">Delete Forever</button>
                    </div>
                  </td>
                </tr>
              ))}
              {reels.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-gray-500">No reels found</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReelsAdmin;
